import React from "react";
import { Link } from "react-router-dom";
import { CITIES } from "@/data/cities";
import { Overline } from "@/components/brand/SectionDivider";
import { MapPin, ArrowUpRight } from "lucide-react";
import { AmbientLight } from "@/components/brand/AmbientLight";
import useDocumentTitle from "@/lib/useDocumentTitle";

export default function FloridaCoverageHub() {
  useDocumentTitle("Florida Coverage | AG Public Adjusting");
  return (
    <main data-testid="coverage-hub-page" className="pt-32 bg-midnight-gradient overflow-hidden">
      <section className="relative py-20 lg:py-28">
        <AmbientLight size={1200} color="blue" intensity={0.4} style={{ top: "-5%", right: "20%" }} />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <Overline className="mb-8">Florida Coverage</Overline>
          <h1 className="font-display-tight text-white text-[clamp(2.8rem,6vw,6rem)] leading-[0.95] tracking-[-0.045em] max-w-4xl">
            Statewide capability.
            <br />
            <span className="gold-metallic font-display font-light">Local discipline.</span>
          </h1>
          <p className="mt-10 text-ag-text-secondary text-lg leading-[1.65] font-light max-w-2xl">
            AG Public Adjusting serves Florida policyholders with focused presence in five launch markets.
            Additional cities are added as the firm expands.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CITIES.map((c, i) => (
              <Link
                key={c.slug}
                to={`/florida-coverage/${c.slug}`}
                data-testid={`coverage-hub-${c.slug}`}
                className="glass block p-10 transition-all duration-500 group hover:border-gold/30 relative overflow-hidden"
              >
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: "radial-gradient(circle, rgba(0,119,255,0.2), transparent)" }} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-mono text-[0.65rem] text-gold tracking-[0.28em]">{String(i + 1).padStart(2, "0")}</span>
                    <MapPin className="w-4 h-4 text-gold" strokeWidth={1.4} />
                  </div>
                  <div className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.22em] uppercase mb-2">{c.region}</div>
                  <h2 className="font-display-tight text-4xl text-white group-hover:gold-metallic group-hover:text-transparent transition-colors mb-4 tracking-[-0.04em] font-light">
                    {c.name}
                  </h2>
                  <p className="text-sm text-ag-text-secondary font-light leading-[1.65] mb-6">{c.intro}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {c.common_claims.map((t) => (
                      <span key={t} className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-ag-text-secondary/70 border border-white/10 px-2.5 py-1">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="inline-flex items-center gap-2 font-mono text-[0.65rem] tracking-[0.22em] uppercase text-gold">
                    Open <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
